import { getTRPCClient } from "@repo/api";
import { appRegisterPatientInputSchema, parseInput } from "@repo/api/schemas";
import { mintEntityId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@repo/ui/form-fields";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import type { OverlayControlProps } from "@/app/overlays/registry";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import {
  EMPTY_RELATED_PERSON_FIELDS,
  RelatedPersonFields,
  type RelatedPersonFieldValues,
  relatedPersonFieldsSchema,
  toRelatedPersonDetails,
} from "./related-person-fields";

const GENDERS = ["unknown", "female", "male", "other"] as const;
type Gender = (typeof GENDERS)[number];

const registrationFormSchema = z
  .object({
    id: z.string().uuid(),
    caseId: z.string().uuid(),
    reviewTaskId: z.string().uuid(),
    name: z
      .array(
        z.object({
          given: z.tuple([z.string().trim().min(1, "Enter a first name"), z.string().trim()]),
          family: z.string().trim().min(1, "Enter a last name"),
        }),
      )
      .min(1),
    preferredName: z.string().trim(),
    birthDate: z
      .date()
      .nullable()
      .refine((date) => date !== null, "Enter a date of birth")
      .refine((date) => !date || date <= new Date(), "Date of birth cannot be in the future"),
    gender: z.string().refine((value) => GENDERS.includes(value as Gender), "Choose a gender"),
    email: z.union([z.string().trim().email("Enter a valid email"), z.literal("")]),
    phone: z.string().trim(),
    addRelatedPerson: z.boolean(),
    relatedPerson: z.custom<RelatedPersonFieldValues>(),
  })
  .superRefine((value, ctx) => {
    if (!value.addRelatedPerson) {
      if (!value.email.trim())
        ctx.addIssue({ code: "custom", path: ["email"], message: "Enter a patient email or add a related person" });
      return;
    }
    const result = relatedPersonFieldsSchema.safeParse(value.relatedPerson);
    if (!result.success)
      for (const issue of result.error.issues)
        ctx.addIssue({ code: "custom", message: issue.message, path: ["relatedPerson", ...issue.path] });
  });

export function RegisterPatientDialog({ open, onClose, onExited }: OverlayControlProps) {
  const navigate = useNavigate();
  const [id] = useState(() => mintEntityId());
  const [caseId] = useState(() => mintEntityId());
  const [reviewTaskId] = useState(() => mintEntityId());
  const [relatedPersonId] = useState(() => mintEntityId());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const form = useAppForm({
    defaultValues: {
      id,
      caseId,
      reviewTaskId,
      name: [{ given: ["", ""], family: "" }],
      preferredName: "",
      birthDate: null as Date | null,
      gender: "" as string,
      email: "",
      phone: "",
      addRelatedPerson: false,
      relatedPerson: EMPTY_RELATED_PERSON_FIELDS,
    },
    // Validate when Register is pressed, never while typing. canSubmitWhenInvalid
    // keeps a leftover error from blocking the re-check on the next attempt.
    canSubmitWhenInvalid: true,
    validators: { onSubmit: registrationFormSchema },
    onSubmitInvalid: focusFirstInvalid,
    onSubmit: async ({ value }) => {
      setPending(true);
      setError(undefined);
      try {
        const date = value.birthDate;
        const legal = value.name[0];
        if (!legal) throw new Error("Enter the patient’s legal name");
        const telecom: { system: "email" | "phone"; value: string }[] = [];
        if (value.email.trim()) telecom.push({ system: "email", value: value.email.trim() });
        if (value.phone.trim()) telecom.push({ system: "phone", value: value.phone.trim() });
        const input = parseInput(appRegisterPatientInputSchema, {
          id,
          caseId,
          reviewTaskId,
          name: [
            {
              use: "official",
              given: legal.given.map((part) => part.trim()).filter(Boolean),
              family: legal.family.trim(),
            },
            ...(value.preferredName.trim() ? [{ use: "usual" as const, given: [value.preferredName.trim()] }] : []),
          ],
          gender: value.gender as Gender,
          birthDate: date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
            : "",
          relatedPerson: value.addRelatedPerson
            ? {
                id: relatedPersonId,
                ...toRelatedPersonDetails(value.relatedPerson),
                isEmergencyContact: value.relatedPerson.isEmergencyContact,
                portalAccess: value.relatedPerson.portalAccess,
              }
            : undefined,
          telecom,
        });
        const result = await getTRPCClient().patient.register.mutate(input);
        if (!result.success) throw new Error(result.error.message);
        onClose();
        if ("patientId" in result.data)
          await navigate({ to: "/patients/$patientId", params: { patientId: result.data.patientId } });
        else await navigate({ to: "/tasks/$taskId/review", params: { taskId: result.data.reviewTaskId } });
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Registration failed. Please try again.");
      } finally {
        setPending(false);
      }
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !pending && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent scrollable className="max-h-[90svh] overflow-hidden sm:max-w-xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>Register patient</DialogTitle>
          <DialogDescription>
            Enter the patient’s details. Possible duplicates are sent to staff for review.
          </DialogDescription>
        </DialogHeader>
        <form
          id="register-patient-form"
          className="-mx-1 min-h-0 space-y-4 overflow-y-auto px-1 py-1"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <fieldset disabled={pending} className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <form.AppField name="name[0].given[0]">
                {(field) => (
                  <field.InputField label="First name" autoComplete="given-name" isRequired disabled={pending} />
                )}
              </form.AppField>
              <form.AppField name="name[0].given[1]">
                {(field) => (
                  <field.InputField label="Middle name (optional)" autoComplete="additional-name" disabled={pending} />
                )}
              </form.AppField>
              <form.AppField name="name[0].family">
                {(field) => (
                  <field.InputField label="Last name" autoComplete="family-name" isRequired disabled={pending} />
                )}
              </form.AppField>
            </div>
            <form.AppField name="preferredName">
              {(field) => (
                <field.InputField label="Preferred name (optional)" autoComplete="nickname" disabled={pending} />
              )}
            </form.AppField>
            <form.Subscribe selector={(state) => state.values.addRelatedPerson}>
              {(hasRelatedPerson) => (
                <form.AppField name="email">
                  {(field) => (
                    <field.InputField
                      label={hasRelatedPerson ? "Email (optional)" : "Email"}
                      type="email"
                      autoComplete="email"
                      isRequired={!hasRelatedPerson}
                      disabled={pending}
                    />
                  )}
                </form.AppField>
              )}
            </form.Subscribe>
            <form.AppField name="phone">
              {(field) => (
                <field.InputField label="Phone (optional)" type="tel" autoComplete="tel" disabled={pending} />
              )}
            </form.AppField>
            <form.AppField name="gender">
              {(field) => (
                <field.OptionGroupField
                  label="Gender"
                  isRequired
                  disabled={pending}
                  options={[
                    { value: "unknown", label: "Unknown" },
                    { value: "female", label: "Female" },
                    { value: "male", label: "Male" },
                    { value: "other", label: "Other" },
                  ]}
                />
              )}
            </form.AppField>
            <form.AppField name="birthDate">
              {(field) => (
                <Field errors={field.state.meta.errors} isTouched={field.state.meta.isTouched} isRequired>
                  <FieldLabel>Date of birth</FieldLabel>
                  <field.PropertyPickerCalendarField
                    placeholder="Date of birth"
                    disabled={pending}
                    showYear
                    calendarProps={{
                      defaultMonth: field.state.value ?? undefined,
                      captionLayout: "dropdown",
                      startMonth: new Date(1900, 0),
                      endMonth: new Date(),
                      disabled: { after: new Date() },
                    }}
                  />
                  <FieldError />
                </Field>
              )}
            </form.AppField>
          </fieldset>
          <form.AppField
            name="addRelatedPerson"
            listeners={{
              onChange: () => {
                if (form.state.submissionAttempts > 0) void form.validate("submit");
              },
            }}
          >
            {(field) => (
              <field.SwitchField
                label="Add a related person"
                description="Add a parent, guardian, or other contact for this patient."
                disabled={pending}
              />
            )}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.addRelatedPerson}>
            {(enabled) =>
              enabled && (
                <fieldset disabled={pending} className="space-y-4 rounded-lg border p-4">
                  <legend className="px-1 text-sm font-medium">Related person</legend>
                  <RelatedPersonFields
                    form={form}
                    fields="relatedPerson"
                    disabled={pending}
                    labelPrefix="Related person"
                  />
                </fieldset>
              )
            }
          </form.Subscribe>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="ghost" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="register-patient-form" disabled={pending}>
            {pending ? "Checking patients…" : "Register patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
